import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import { getRewardfulReferral } from "../../utils/rewardful";
import { track } from "../../utils/analytics";
import { getRegion } from "../../utils/region";
import { money } from "./HardPaywall";
import "./HardPaywall.css";

// Mid-$1-trial accounts that run out of credits (or tap Upgrade) pick how many
// credits they want, instead of the old single "Start Pro now" button. Trial
// users who didn't need 300 credits were converted to Pro anyway, used it up
// and complained; this lets them choose after trying it (the Opus Clip move).
// Same light look as the hard paywall.
//
// Pro monthly ends the trial on the same subscription (/trial/start-now). Any
// other plan goes through Checkout as a new subscription; the checkout webhook
// cancels the trial (stripeService.cancelReplacedPlanSubscriptions).

// Prices match config/planLimits.js — what Stripe actually charges. Pro comes
// in packs of 300 credits at the full Pro price each (stripeService.proPackItem).
// Starter is monthly only (as on OpusClip): on Annual its card is unavailable.
const PRO_MAX_PACKS = 10;
const PLANS = [
  { key: "Starter", credits: 150, monthly: 15, annual: null, hours: "≈ 2.5 hours of video" },
  { key: "Pro", credits: 300, monthly: 29, annual: 174, hours: "≈ 5 hours of video", badge: "MOST POPULAR" },
  { key: "Premium", credits: 1500, monthly: 145, annual: 870, hours: "≈ 25 hours of video", badge: "MOST CREDITS" },
];

const TrialPlanPicker = ({ trial, message, onClose, onBuyCredits }) => {
  const { t } = useTranslation("paywall");
  const [interval, setInterval_] = useState("monthly");
  const [planKey, setPlanKey] = useState("Pro");
  // Annual has no Starter: switching to it moves a Starter pick to Pro.
  const setBillingInterval = (iv) => {
    setInterval_(iv);
    if (iv === "annual" && planKey === "Starter") setPlanKey("Pro");
  };
  const [packs, setPacks] = useState(1); // Pro only
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { track("trial_plan_picker_viewed"); }, []);

  // Checkout applies the regional (PPP) coupon server-side; show the same price.
  const [discountPct, setDiscountPct] = useState(0);
  useEffect(() => {
    let cancelled = false;
    getRegion().then((r) => { if (!cancelled && r.discountPct > 0) setDiscountPct(r.discountPct); });
    return () => { cancelled = true; };
  }, []);
  const off = (n) => Math.round(n * (100 - discountPct)) / 100;

  const annual = interval === "annual";
  const plan = PLANS.find((p) => p.key === planKey);
  const startsProNow = planKey === "Pro" && !annual && packs === 1;
  // "Start Pro now" charges whatever the trial converts at (its own discount).
  const proNowPrice = trial?.chargeCents ? trial.chargeCents / 100 : off(29);
  const n = (p) => (p.key === "Pro" ? packs : 1);
  const listMonthly = (p) => (annual ? p.annual / 12 : p.monthly) * n(p);
  const shownMonthly = (p) => (p.key === "Pro" && !annual && packs === 1 ? proNowPrice : off(listMonthly(p)));
  const todayTotal = startsProNow ? proNowPrice : off((annual ? plan.annual : plan.monthly) * n(plan));
  const trialEnd = trial?.trialEnd
    ? new Date(trial.trialEnd).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  const choose = async () => {
    setError("");
    setLoading(true);
    track("checkout_started", { plan: `${planKey.toLowerCase()}_${interval}`, value: todayTotal, from: "trial_picker" });
    try {
      if (startsProNow) {
        await axiosInstance.post("/trial/start-now");
        window.location.href = "/home?pro=started";
        return;
      }
      const referral = getRewardfulReferral();
      const res = await axiosInstance.post("/billing/plan/checkout", {
        planName: planKey,
        interval,
        ...(planKey === "Pro" ? { credits: plan.credits * packs } : {}),
        ...(referral ? { referral } : {}),
      });
      if (res.data?.url) { window.location.href = res.data.url; return; }
      setError(t("err_checkout"));
    } catch (err) {
      setError(err.response?.data?.message || t("err_checkout"));
    }
    setLoading(false);
  };

  return (
    <div className="hard-paywall" role="dialog" aria-modal="true" aria-label="Choose your plan">
      <span className="hpw-blob hpw-blob--a" aria-hidden="true" />
      <span className="hpw-blob hpw-blob--b" aria-hidden="true" />
      <div className="hard-paywall-card">
        <div className="hard-paywall-topbar">
          <button type="button" className="hard-paywall-close" onClick={onClose} aria-label={t("close")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
          <span />
        </div>

        <div className="hpw-mascot hpw-mascot--sm"><img src="/mascot.png" alt="Clipbait" /></div>

        <h1 className="hard-paywall-title">
          Pick how many <span className="hard-paywall-accent">credits</span> you need
        </h1>
        <p className="hpw-proof">
          {message || (trialEnd
            ? `Your trial runs until ${trialEnd}. Choose a plan now to keep clipping.`
            : "Choose a plan to keep clipping.")}
        </p>

        <div className="hpw-interval" role="radiogroup" aria-label="Billing">
          {["monthly", "annual"].map((iv) => (
            <button
              key={iv}
              type="button"
              role="radio"
              aria-checked={interval === iv}
              className={`hpw-interval-btn${interval === iv ? " is-on" : ""}`}
              onClick={() => setBillingInterval(iv)}
            >
              {iv === "monthly" ? "Monthly" : <>Annual <span className="hpw-interval-save">up to 60% off</span></>}
            </button>
          ))}
        </div>

        <div className="hard-paywall-plans" role="radiogroup" aria-label="Plan">
          {PLANS.map((p) => (annual && !p.annual) ? (
            <div key={p.key} className="hard-paywall-plan is-unavailable" aria-disabled="true">
              <span className="hpw-radio" />
              <span className="hpw-plan-text">
                <span className="hpw-plan-name">{p.key}</span>
                <span className="hpw-plan-note">{p.credits.toLocaleString()} credits/mo · {p.hours}</span>
              </span>
              <span className="hpw-plan-price"><span>Monthly only</span></span>
            </div>
          ) : (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={planKey === p.key}
              className={`hard-paywall-plan${planKey === p.key ? " is-selected" : ""}`}
              onClick={() => setPlanKey(p.key)}
            >
              {p.badge && <span className="hpw-badge">{p.badge}</span>}
              <span className="hpw-radio" />
              <span className="hpw-plan-text">
                <span className="hpw-plan-name">{p.key}{n(p) > 1 ? ` ×${n(p)}` : ""}</span>
                <span className="hpw-plan-note">
                  {(p.credits * n(p)).toLocaleString()} credits/mo · {n(p) > 1 ? `≈ ${5 * n(p)} hours of video` : p.hours}
                </span>
              </span>
              <span className="hpw-plan-price">
                {discountPct > 0 && <s className="hpw-plan-was">{money(listMonthly(p))}</s>}
                {money(shownMonthly(p))}<span>{annual ? `/mo · ${money(off(p.annual * n(p)))}/yr` : "/mo"}</span>
                {/* Packs multiply the price; keep the per-pack $29 in view. */}
                {n(p) > 1 && <span>{money(off(annual ? p.annual / 12 : p.monthly))} × {n(p)} packs</span>}
              </span>
            </button>
          ))}
        </div>

        {planKey === "Pro" && (
          <div className="hpw-packs">
            <span className="hpw-packs-label">Need more? Add Pro packs</span>
            <div className="hpw-packs-stepper">
              <button type="button" aria-label="Fewer packs" disabled={packs <= 1} onClick={() => setPacks(packs - 1)}>−</button>
              <span>{packs} {packs === 1 ? "pack" : "packs"} · {(300 * packs).toLocaleString()} credits</span>
              <button type="button" aria-label="More packs" disabled={packs >= PRO_MAX_PACKS} onClick={() => setPacks(packs + 1)}>+</button>
            </div>
          </div>
        )}

        {error && <div className="hard-paywall-error">{error}</div>}

        <button type="button" className="hard-paywall-cta" onClick={choose} disabled={loading}>
          <span className="hard-paywall-cta-shine" aria-hidden="true" />
          <span className="hard-paywall-cta-label">
            {loading ? t("cta_loading") : startsProNow ? "Start Pro now"
              : `Get ${planKey}${n(plan) > 1 ? ` ×${n(plan)}` : ""}${annual ? " Annual" : ""}`}
          </span>
        </button>

        <p className="hard-paywall-fineprint">
          {annual
            ? `${money(todayTotal)} today for the year · your trial ends and ${planKey} starts now · cancel anytime`
            : `${money(todayTotal)} today · your trial ends and ${planKey} starts now · renews monthly · cancel anytime`}
        </p>
        {onBuyCredits && (
          <button type="button" className="hpw-credits-link" onClick={onBuyCredits}>
            Or buy a one-time credit pack
          </button>
        )}
      </div>
    </div>
  );
};

export default TrialPlanPicker;
